import React, { useRef, useState } from 'react';
import { SeriesPoint, DistributionPoint } from '../api/dashboard';

/** 'YYYY-MM-DD' → 'Jul 1'. */
const shortDate = (iso?: string): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface TrendChartProps {
  title: string;
  points: SeriesPoint[];
  color?: string;
  /** Formats the total/peak values (e.g. money vs plain count). */
  format?: (n: number) => string;
}

/**
 * Compact 30-day trend as an SVG area + line, with the window total and peak.
 * Hand-rolled (no chart dependency) to match the rest of the app.
 */
/** Height of the plotting area, in px. Y-axis ticks align to this. */
const PLOT_H = 132;

export const TrendChart: React.FC<TrendChartProps> = ({
  title,
  points,
  color = 'var(--brand)',
  format = (n) => String(n),
}) => {
  const values = points.map((p) => p.value);
  const total = values.reduce((a, b) => a + b, 0);
  const peak = values.length ? Math.max(...values) : 0;
  const max = Math.max(1, peak);

  const W = 300;
  const H = 100; // viewBox units; scaled to PLOT_H by the height attribute
  const pad = 4;
  const stepX = points.length > 1 ? W / (points.length - 1) : W;
  const coords = points.map((p, i) => ({
    x: i * stepX,
    y: H - pad - (p.value / max) * (H - pad * 2),
  }));
  const line = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
    .join(' ');
  const area = coords.length ? `${line} L${W},${H} L0,${H} Z` : '';

  // Y-axis ticks, top (max) → bottom (0), matching the gridlines below.
  const ticks = [max, max / 2, 0];
  const gridYs = [pad, H / 2, H - pad];

  const plotRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  /** Map the pointer's x to the nearest data index. */
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = plotRef.current;
    if (!el || points.length === 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    const i = Math.round(ratio * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  };

  const hovered =
    hover !== null && points[hover]
      ? {
          point: points[hover],
          leftPct: points.length > 1 ? (hover / (points.length - 1)) * 100 : 50,
          // viewBox y → px, mirroring how the path is drawn.
          topPx:
            ((H - pad - (points[hover].value / max) * (H - pad * 2)) / H) * PLOT_H,
        }
      : null;

  return (
    <div className="card p-5">
      <div className="text-[13px] font-semibold text-g500 mb-1">{title}</div>
      <div className="text-[26px] font-extrabold text-ink leading-none mb-4">
        {format(total)}
      </div>

      <div className="flex">
        {/* Y axis — counts, so the plot is readable without hovering */}
        <div
          className="flex flex-col justify-between items-end shrink-0"
          style={{ height: PLOT_H, paddingRight: 8, minWidth: 34 }}
        >
          {ticks.map((t, i) => (
            <span key={i} className="text-[11px] text-g400 font-medium leading-none">
              {format(Math.round(t))}
            </span>
          ))}
        </div>

        {/*
         * Hover layer wraps only the plot (not the axis gutter) so the pointer
         * x maps straight onto the data index. The marker and tooltip are HTML
         * rather than SVG: the viewBox is stretched with preserveAspectRatio
         * "none", which would squash an SVG circle into an ellipse.
         */}
        <div
          ref={plotRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
          style={{ position: 'relative', flex: 1, minWidth: 0, height: PLOT_H, cursor: 'crosshair' }}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            width="100%"
            height={PLOT_H}
            style={{ display: 'block', overflow: 'visible' }}
            aria-hidden="true"
          >
            {gridYs.map((y, i) => (
              <line
                key={i}
                x1={0}
                x2={W}
                y1={y}
                y2={y}
                stroke="var(--g200)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {area && <path d={area} fill={color} opacity={0.12} />}
            {line && (
              <path
                d={line}
                fill="none"
                stroke={color}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
          </svg>

          {hovered && (
            <>
              {/* Crosshair */}
              <div
                style={{
                  position: 'absolute',
                  left: `${hovered.leftPct}%`,
                  top: 0,
                  height: PLOT_H,
                  width: 1,
                  background: 'var(--g300, var(--g200))',
                  pointerEvents: 'none',
                }}
              />
              {/* Point marker */}
              <div
                style={{
                  position: 'absolute',
                  left: `${hovered.leftPct}%`,
                  top: hovered.topPx,
                  width: 9,
                  height: 9,
                  marginLeft: -4.5,
                  marginTop: -4.5,
                  borderRadius: '50%',
                  background: color,
                  border: '2px solid #fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  pointerEvents: 'none',
                }}
              />
              {/* Tooltip — anchors inward near the edges so it never clips */}
              <div
                style={{
                  position: 'absolute',
                  left: `${hovered.leftPct}%`,
                  top: hovered.topPx - 10,
                  transform:
                    hovered.leftPct < 18
                      ? 'translate(0, -100%)'
                      : hovered.leftPct > 82
                      ? 'translate(-100%, -100%)'
                      : 'translate(-50%, -100%)',
                  background: 'var(--ink)',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '6px 9px',
                  fontSize: 11.5,
                  lineHeight: 1.35,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
                  zIndex: 2,
                }}
              >
                <div style={{ opacity: 0.75 }}>{shortDate(hovered.point.date)}</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {format(hovered.point.value)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* X axis labels sit under the plot, aligned past the Y-axis gutter */}
      <div
        className="flex justify-between text-[11px] text-g400 mt-2"
        style={{ paddingLeft: 42 }}
      >
        <span>{shortDate(points[0]?.date)}</span>
        <span>peak {format(peak)}</span>
        <span>{shortDate(points[points.length - 1]?.date)}</span>
      </div>
    </div>
  );
};

interface DistributionBarsProps {
  title: string;
  points: DistributionPoint[];
  color?: string;
}

/** Status distribution as labelled horizontal bars. */
export const DistributionBars: React.FC<DistributionBarsProps> = ({
  title,
  points,
  color = 'var(--brand)',
}) => {
  const max = Math.max(1, ...points.map((p) => p.value));
  const total = points.reduce((a, p) => a + p.value, 0);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-semibold text-g500">{title}</span>
        <span className="text-[12px] text-g400">{total} total</span>
      </div>
      {total === 0 ? (
        <div className="text-[13px] text-g400">No data yet.</div>
      ) : (
        points.map((p) => (
          <div key={p.label} className="mb-2.5 last:mb-0">
            <div className="flex justify-between text-[12px] mb-1">
              <span className="capitalize text-g600">{p.label.replace(/_/g, ' ')}</span>
              <span className="font-semibold">{p.value}</span>
            </div>
            <div className="h-1.5 rounded" style={{ background: 'var(--g100)' }}>
              <div
                className="h-full rounded"
                style={{ width: `${(p.value / max) * 100}%`, background: color }}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
};
